import{
  WHITE,
  BLACK,
  legalChessMoves,
  applyChessMove,
  chessGameState
}from'./internationalChessEngine';

/*
 * 国际象棋 AI 1~10级
 *
 * 黑方 = AI
 * 正分对白方有利
 * 负分对黑方有利
 */

const VALUE={
  p:100,
  n:320,
  b:330,
  r:500,
  q:900,
  k:20000
};

/*
 * 难度配置
 *
 * depth：搜索深度
 * pool：允许从前多少个候选中选择
 * noise：评分误差
 * branch：深层搜索最大候选数
 */
const CONFIG={
  1:{depth:1,pool:8,noise:220,branch:12},
  2:{depth:1,pool:6,noise:160,branch:12},
  3:{depth:1,pool:5,noise:110,branch:14},
  4:{depth:2,pool:5,noise:80, branch:14},
  5:{depth:2,pool:4,noise:55, branch:16},
  6:{depth:2,pool:3,noise:35, branch:18},
  7:{depth:3,pool:3,noise:24, branch:16},
  8:{depth:3,pool:2,noise:12, branch:18},
  9:{depth:3,pool:1,noise:4,  branch:20},
 10:{depth:4,pool:1,noise:0,  branch:14}
};

function clampLevel(level){
  return Math.max(
    1,
    Math.min(10,Number(level)||1)
  );
}

function material(board){
  let score=0;

  for(let r=0;r<8;r++){
    for(let c=0;c<8;c++){
      const p=board[r][c];

      if(!p)continue;

      const v=VALUE[p[1]]||0;

      score+=
        p[0]===WHITE
          ?v
          :-v;
    }
  }

  return score;
}

function centerBonus(board){
  let score=0;

  for(let r=0;r<8;r++){
    for(let c=0;c<8;c++){
      const p=board[r][c];

      if(!p)continue;

      const type=p[1];

      if(type==='k'||type==='p'){
        continue;
      }

      const distance=
        Math.abs(3.5-r)+
        Math.abs(3.5-c);

      const bonus=
        Math.max(0,7-distance)*2;

      score+=
        p[0]===WHITE
          ?bonus
          :-bonus;
    }
  }

  return score;
}

function pawnAdvance(board){
  let score=0;

  for(let r=0;r<8;r++){
    for(let c=0;c<8;c++){
      const p=board[r][c];

      if(p==='wp'){
        score+=(6-r)*3;
      }

      if(p==='bp'){
        score-=(r-1)*3;
      }
    }
  }

  return score;
}

function evaluate(
  board,
  turn,
  state
){
  const gs=chessGameState(
    board,
    turn,
    state
  );

  if(gs.over){
    if(gs.winner===WHITE){
      return 1000000;
    }

    if(gs.winner===BLACK){
      return -1000000;
    }

    return 0;
  }

  let score=
    material(board)+
    centerBonus(board)+
    pawnAdvance(board);

  /*
   * 少量机动性评分
   */
  const mobility=
    legalChessMoves(
      board,
      turn,
      state
    ).length;

  score+=
    turn===WHITE
      ?mobility
      :-mobility;

  if(gs.check){
    score+=
      turn===WHITE
        ?-30
        :30;
  }

  return score;
}

function moveScore(
  board,
  move,
  state,
  turn
){
  const target=
    board[move.to[0]][move.to[1]];

  let score=0;

  if(target){
    score+=VALUE[target[1]]||0;
  }

  if(move.promotion){
    score+=800;
  }

  if(move.castle){
    score+=45;
  }

  const nb=applyChessMove(
    board,
    move,
    state
  );

  score+=
    turn===WHITE
      ?evaluate(
          nb,
          BLACK,
          move.nextState||state
        )/100
      :-evaluate(
          nb,
          WHITE,
          move.nextState||state
        )/100;

  return score;
}

function orderedMoves(
  board,
  turn,
  state,
  branch
){
  const moves=legalChessMoves(
    board,
    turn,
    state
  );

  const sorted=moves
    .map(move=>({
      move,
      score:moveScore(
        board,
        move,
        state,
        turn
      )
    }))
    .sort(
      (a,b)=>b.score-a.score
    )
    .map(x=>x.move);

  if(
    branch&&
    sorted.length>branch
  ){
    return sorted.slice(0,branch);
  }

  return sorted;
}

function search(
  board,
  turn,
  state,
  depth,
  alpha,
  beta,
  cfg
){
  const gs=chessGameState(
    board,
    turn,
    state
  );

  if(
    depth<=0||
    gs.over
  ){
    return evaluate(
      board,
      turn,
      state
    );
  }

  const moves=orderedMoves(
    board,
    turn,
    state,
    cfg.branch
  );

  if(turn===WHITE){
    let best=-Infinity;

    for(const move of moves){
      const nb=applyChessMove(
        board,
        move,
        state
      );

      const ns=
        move.nextState||
        state;

      best=Math.max(
        best,
        search(
          nb,
          BLACK,
          ns,
          depth-1,
          alpha,
          beta,
          cfg
        )
      );

      alpha=Math.max(
        alpha,
        best
      );

      if(beta<=alpha){
        break;
      }
    }

    return best;
  }

  let best=Infinity;

  for(const move of moves){
    const nb=applyChessMove(
      board,
      move,
      state
    );

    const ns=
      move.nextState||
      state;

    best=Math.min(
      best,
      search(
        nb,
        WHITE,
        ns,
        depth-1,
        alpha,
        beta,
        cfg
      )
    );

    beta=Math.min(
      beta,
      best
    );

    if(beta<=alpha){
      break;
    }
  }

  return best;
}

export function chooseInternationalChessAiMove(
  board,
  state,
  level=5
){
  const lv=clampLevel(level);
  const cfg=CONFIG[lv];

  const moves=legalChessMoves(
    board,
    BLACK,
    state
  );

  if(!moves.length){
    return null;
  }

  const scored=moves
    .map(move=>{
      const nb=applyChessMove(
        board,
        move,
        state
      );

      const ns=
        move.nextState||
        state;

      let score=search(
        nb,
        WHITE,
        ns,
        cfg.depth-1,
        -Infinity,
        Infinity,
        cfg
      );

      /*
       * AI执黑：
       * 分数越低，对AI越有利。
       *
       * 低等级加入判断误差。
       */
      if(cfg.noise){
        score+=
          (Math.random()*2-1)*
          cfg.noise;
      }

      return{
        move,
        score
      };
    })
    .sort(
      (a,b)=>a.score-b.score
    );

  /*
   * 低等级会在若干候选着中随机选择。
   * 9、10级基本选择搜索得到的最佳着。
   */
  const pool=Math.min(
    cfg.pool,
    scored.length
  );

  const index=
    pool<=1
      ?0
      :Math.floor(
          Math.random()*pool
        );

  return scored[index].move;
}

export const INTERNATIONAL_CHESS_LEVELS=[
  '',
  '入门Ⅰ',
  '入门Ⅱ',
  '初级Ⅰ',
  '初级Ⅱ',
  '中级Ⅰ',
  '中级Ⅱ',
  '高级Ⅰ',
  '高级Ⅱ',
  '棋手',
  '高手'
];
